// src/components/wellfinder/UndoToast.jsx
//
// The safety net that replaces confirmation dialogs.
//
// At three seconds a well, a modal on every commit costs more than every
// mis-key it would prevent. This costs nothing on the happy path — it appears,
// it is ignored, it expires — and it is a BETTER answer than a modal, because
// reopen is honest: the verdict is withdrawn, the values and their provenance
// are kept, the published correlation is downgraded, and the log records both
// the commit and the undo. A modal would have pretended the record was never
// touched.
//
// Five seconds. Long enough to notice a wrong key and reach for it, short
// enough that `u` cannot resurrect something from ten minutes and forty records
// ago — which would be a different and much worse mistake.

import { useEffect, useState } from "react";
import { Undo2 } from "lucide-react";
import { useWfQueueStore } from "../../stores/useWfQueueStore";

const WINDOW_MS = 5000;

export default function UndoToast() {
  const lastCommit = useWfQueueStore((s) => s.lastCommit);
  const undoLastCommit = useWfQueueStore((s) => s.undoLastCommit);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!lastCommit) { setVisible(false); return; }
    setVisible(true);
    const t = setTimeout(() => {
      setVisible(false);
      // Clear the target as well as the toast: `u` must not reopen a record the
      // analyst stopped thinking about four records back.
      if (useWfQueueStore.getState().lastCommit?.at === lastCommit.at) {
        useWfQueueStore.setState({ lastCommit: null });
      }
    }, WINDOW_MS);
    return () => clearTimeout(t);
  }, [lastCommit]);

  if (!visible || !lastCommit) return null;

  return (
    <div
      data-testid="undo-toast"
      // bottom-full, NOT bottom-4. It was anchored to the page root, which put
      // it 16px from the viewport bottom — squarely inside the command bar's
      // band, sitting on top of the commit buttons. z-30 meant it painted over
      // them rather than under, so it was there and read as part of the bar.
      //
      // bottom-full against a `relative` bar row puts it immediately ABOVE the
      // bar whatever the bar's height, which changes on every commit error and
      // whenever the reject picker opens. No magic number to drift, and no
      // layout shift when it appears — the same reasoning that took the bar out
      // of the scroll container.
      className="absolute bottom-full left-1/2 z-30 mb-2 -translate-x-1/2 rounded border border-bg-border bg-bg-soft px-3 py-2 shadow-lg"
    >
      <div className="flex items-center gap-3 text-xs">
        <span className="text-fg-secondary">
          {lastCommit.verdict === "approved" ? "Approved" :
           lastCommit.verdict === "rejected" ? "Rejected" : "Flagged"}
          {" · "}committed
          {/* A move changed two records; the undo gives the well back, so say
              which record that is before the analyst decides. */}
          {lastCommit.transfer && (
            <span className="text-yellow-300"> · moved from {lastCommit.transfer.holderLabel}</span>
          )}
        </span>
        <button
          type="button"
          data-testid="undo-button"
          onClick={() => undoLastCommit()}
          className="flex items-center gap-1 rounded px-2 py-0.5 text-fg-primary ring-1 ring-white/20 hover:bg-white/[0.08]"
        >
          <Undo2 size={11} />
          Undo
          <kbd className="ml-1 rounded border border-white/20 bg-white/[0.06] px-1 text-[10px] leading-4 text-fg-secondary">
            u
          </kbd>
        </button>
      </div>
    </div>
  );
}
